// src/components/ProtectedRoute/Admin/Dialogs/parts/ScientificNameSelector.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Stack,
  Button,
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DialogScientificNameEditor from "../DialogScientificNameEditor";
import supabase from "../../../../../utils/supabase";
import { extractAuthorNames, formatScientificNameLabel } from "../../../../../utils/scientificNameFormatter";
import { normalizeSearchQuery, searchScientificNames } from "../../../../../utils/scientificNameSearch";
import TuneIcon from "@mui/icons-material/Tune";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * 幅いっぱい＆縦配置対応の学名セレクタ
 */
export default function ScientificNameSelector({
  onChange,
  onRefreshData,
  value = "",
  valueId = "",
  selectedId: selectedIdProp = "",
  initialValueId = "",
  scientificNameData = [],
  isRequired = false,
  disabled = false,

  // UI
  label = "Scientific Name",
  placeholder = "Search by name / id",
  size = "small",
  showInputLabel = false, // true: TextFieldのlabelとして表示
  inlineAdd = true,       // ラベル行右に「Add Name」を表示
  fullWidth = true,       // 幅いっぱいに広げる
  sx = {},                // 追加スタイル（rootに適用）
}) {
  const [selectedId, setSelectedId] = useState(
    (selectedIdProp || valueId || value || initialValueId || "") ? String(selectedIdProp || valueId || value || initialValueId || "") : ""
  );
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const LOG_TAG = "[ScientificNameSelector]";
  const selectedMatches = useCallback(
    () => selectedOptionRef.current && selectedOptionRef.current.id === selectedId,
    [selectedId]
  );
  const openSearchDialog = () => {
    setShowResultsDialog(true);
    if (inputValue) handleSearch();
  };
  const [combineMode, setCombineMode] = useState("AND");
  const [matchMode, setMatchMode] = useState("partial");
  const [fuzzyEnabled, setFuzzyEnabled] = useState(true);
  const selectedOptionRef = React.useRef(null);
  const requestIdRef = React.useRef(0);
  const mergePreferRich = (arr, incoming) => {
    const incomingId = String(incoming.id);
    const idx = arr.findIndex((x) => String(x.id) === incomingId);
    if (idx === -1) return [...arr, incoming];
    const cur = arr[idx];
    const prefer = {
      ...cur,
      ...incoming,
      _parent_label: incoming._parent_label || cur._parent_label || "",
      authority_year: incoming.authority_year || cur.authority_year || "",
      name_spell_valid: incoming.name_spell_valid || cur.name_spell_valid,
      name_spell_original: incoming.name_spell_original || cur.name_spell_original,
      _search_blob: incoming._search_blob || cur._search_blob || "",
      _search_strings: incoming._search_strings || cur._search_strings || [],
      _authority_authors: incoming._authority_authors || cur._authority_authors || [],
    };
    const next = [...arr];
    next[idx] = prefer;
    return next;
  };

  const normalizeOption = useCallback((o) => {
    if (!o) return o;
    return { ...o, id: String(o.id) };
  }, []);

  const fetchOptions = useCallback(
    async (q, { offset = 0, append = false } = {}) => {
      const reqId = ++requestIdRef.current;
      const normalized = normalizeSearchQuery(q);
      console.debug(LOG_TAG, "fetchOptions:start", {
        reqId,
        raw: q,
        normalized,
        append,
        offset,
        prevOptions: options.length,
      });
      if (!normalized) {
        setOptions([]);
        setHasMore(false);
        console.debug(LOG_TAG, "fetchOptions:empty-query, cleared options");
        return;
      }
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setOverlayLoading(true);
        setOptions([]);
        setHasMore(false);
      }
      try {
        const { results, hasMore: more } = await searchScientificNames({
          query: normalized,
          combineMode,
          matchMode,
          allowFuzzy: fuzzyEnabled,
          limit: 50,
          offset,
        });
        if (reqId !== requestIdRef.current) {
          console.debug(LOG_TAG, "fetchOptions:stale-response ignored", { reqId, current: requestIdRef.current });
          return;
        }
        setHasMore(!!more);
        if (append) {
          setOptions((prev) => {
            const base =
              selectedMatches() && selectedOptionRef.current
                ? mergePreferRich(prev, selectedOptionRef.current)
                : prev;
            const merged = results
              .map(normalizeOption)
              .reduce((arr, item) => mergePreferRich(arr, item), base);
            return merged;
          });
        } else {
          if (!results.length) {
            setOptions([]);
            setHasMore(false);
            console.debug(LOG_TAG, "fetchOptions:no-results, cleared options", { reqId });
          } else {
            let merged = results.map(normalizeOption);
            if (selectedMatches() && selectedOptionRef.current) {
              merged = mergePreferRich(merged, selectedOptionRef.current);
            }
            setOptions(merged);
          }
        }
        console.debug(LOG_TAG, "fetchOptions:done", {
          reqId,
          results: results.length,
          appliedOffset: offset,
          append,
          optionsCount: results.length,
          hasMore: !!more,
        });
      } catch (e) {
        console.warn("[ScientificNameSelector] search failed", e?.message || e);
        setOptions(selectedOptionRef.current ? [selectedOptionRef.current] : []);
        setHasMore(false);
      } finally {
        if (reqId !== requestIdRef.current) return;
        setLoading(false);
        setOverlayLoading(false);
        setLoadingMore(false);
      }
    },
    [combineMode, matchMode, fuzzyEnabled, mergePreferRich, normalizeOption, options.length, selectedMatches]
  );
  const handleSearch = useCallback(async () => {
    setHasMore(false);
    await fetchOptions(inputValue, { offset: 0, append: false });
    setShowResultsDialog(true);
  }, [fetchOptions, inputValue]);
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    await fetchOptions(inputValue, { offset: options.length, append: true });
  }, [fetchOptions, hasMore, inputValue, loadingMore, options.length]);

  // 外部から value / valueId が変わったときに state を同期
  useEffect(() => {
    const next = selectedIdProp || valueId || value || initialValueId || "";
    setSelectedId(next ? String(next) : "");
  }, [selectedIdProp, valueId, value, initialValueId]);

  // scientificNameData にある場合、即座にセット
  useEffect(() => {
    if (!selectedId || !Array.isArray(scientificNameData) || !scientificNameData.length) return;
    const hit = scientificNameData.find((n) => String(n.id) === selectedId);
    if (!hit) return;
    const normalized = normalizeOption(hit);
    selectedOptionRef.current = normalized;
    setOptions((prev) => (prev.some((o) => o.id === normalized.id) ? prev : [normalized, ...prev]));
  }, [scientificNameData, normalizeOption, selectedId]);

  // 最低限のプレースホルダを確保（idだけでも表示できるように）
  useEffect(() => {
    if (!selectedId || selectedOptionRef.current) return;
    const minimal = { id: selectedId, name_spell_valid: selectedId };
    selectedOptionRef.current = minimal;
    setOptions((prev) => (prev.some((o) => o.id === minimal.id) ? prev : [minimal, ...prev]));
  }, [selectedId]);

  // selectedId はあるが options / ref に無い場合、単発 fetch で補う
  useEffect(() => {
    const hasOption = options.some((o) => o.id === selectedId);
    if (!selectedId || hasOption || selectedOptionRef.current?.id === selectedId) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("scientific_names")
          .select(`id, name_spell_valid, name_spell_original, authority_year, current_rank, current_parent,
                   scientific_name_and_author!left(author_order, researcher:researcher_id(last_name, first_name))`)
          .eq("id", selectedId)
          .maybeSingle();
        if (error || !data) return;
        let withParent = {
          ...data,
          id: String(data.id),
          _authority_authors: Array.isArray(data.scientific_name_and_author)
            ? data.scientific_name_and_author
                .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0))
                .map((x) => [x.researcher?.last_name, x.researcher?.first_name].filter(Boolean).join(", "))
                .filter(Boolean)
            : [],
        };
        if (data.current_parent) {
          const { data: p } = await supabase
            .from("scientific_names")
            .select("id, name_spell_valid")
            .eq("id", data.current_parent)
            .maybeSingle();
          if (p) {
            withParent._parent_label = `${p.name_spell_valid || p.id}`;
          }
        }
        selectedOptionRef.current = withParent;
        setOptions((prev) => (prev.some((o) => o.id === withParent.id) ? prev : [withParent, ...prev]));
      } catch (e) {
        console.warn("[ScientificNameSelector] fetch by id failed", e);
      }
    })();
  }, [selectedId, options]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === selectedId) || selectedOptionRef.current || null,
    [options, selectedId]
  );
  const hasSelection = Boolean(selectedOption);

  // keep the selected option even when options are cleared
  useEffect(() => {
    if (selectedOption) {
      selectedOptionRef.current = selectedOption;
      // ensure options contains the selected one
      setOptions((prev) => {
        const exists = prev.some((o) => o.id === selectedOption.id);
        return exists ? prev : [selectedOption, ...prev];
      });
    }
  }, [selectedOption]);

  const openAddModal  = () => setIsAddOpen(true);
  const closeAddModal = async (didAdd) => {
    setIsAddOpen(false);
    if (didAdd && typeof onRefreshData === "function") await onRefreshData();
  };

  const formatLabel = useCallback((opt) => {
    if (!opt) return "";
    return formatScientificNameLabel(opt);
  }, []);

  const getLabelString = useCallback((opt) => formatLabel(opt), [formatLabel]);

  const applySelection = useCallback(
    (val) => {
      const id = val?.id != null ? String(val.id) : "";
      const normalized = val ? normalizeOption(val) : null;
      setSelectedId(id);
      onChange?.(id);
      if (normalized) selectedOptionRef.current = normalized;
      setInputValue(normalized ? getLabelString(normalized) : "");
      if (normalized) {
        setOptions((prev) => {
          if (prev.some((o) => o.id === normalized.id)) return prev;
          return [normalized, ...prev];
        });
      }
    },
    [getLabelString, normalizeOption, onChange]
  );

  const renderOptionRow = useCallback((opt) => {
    const parentName = opt._parent_label || opt.current_parent || "";
    return (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {opt.current_rank}: {parentName}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {opt.name_spell_valid || opt.name_spell_original || opt.id}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {(() => {
            const ay = opt.authority_year || "";
            const auArr = Array.isArray(opt._authority_authors) ? opt._authority_authors : [];
            const parts = [];
            if (auArr.length) parts.push(auArr.join(", "));
            if (ay) parts.push(ay);
            return parts.length ? `[${parts.join(", ")}]` : "";
          })()}
        </Typography>
      </Box>
    );
  }, []);

  return (
    <Box sx={{ width: 1, display: "block", ...sx }}>
      {/* ラベル行（右に Add Name） */}
      {inlineAdd || !showInputLabel ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">
            {label}{isRequired ? " *" : ""}
          </Typography>
          <Button size="small" onClick={openAddModal} startIcon={<AddIcon />} sx={{ minWidth: 0, px: 1 }}>
            {/* 先頭の「+」は付けない → 重複回避 */}
            Add Name
          </Button>
        </Stack>
      ) : null}

      <Box sx={{ mb: 1 }}>
        <Tooltip title="Click to change the selected name / ここをクリックすると他の学名に変更できます">
          <span>
            <Button
              fullWidth
              variant={hasSelection ? "outlined" : "contained"}
              color="primary"
              startIcon={<SearchIcon />}
              onClick={openSearchDialog}
              sx={{ justifyContent: "center" }}
              disabled={disabled}
            >
              <Box
                component="span"
                sx={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hasSelection ? `Selected name: ${formatLabel(selectedOption)}` : "Search Names"}
              </Box>
            </Button>
          </span>
        </Tooltip>
      </Box>

      {!inlineAdd && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={openAddModal}>
            Add Name
          </Button>
        </Stack>
      )}

      {isAddOpen && (
        <DialogScientificNameEditor open mode="add" onClose={(didAdd) => closeAddModal(didAdd)} />
      )}

      {overlayLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "linear-gradient(135deg, rgba(0,0,0,0.4), rgba(0,0,0,0.65))",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress size={48} />
          <Typography color="common.white" variant="body1" sx={{ fontWeight: 600 }}>
            Searching scientific names...
          </Typography>
          <Typography color="common.white" variant="caption" sx={{ opacity: 0.8 }}>
            Please hold on while we explore the taxonomy
          </Typography>
        </Box>
      )}

      <Dialog
        open={showResultsDialog}
        onClose={() => setShowResultsDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}
        >
          <Typography variant="h6">Name Selector</Typography>
          <IconButton size="small" onClick={() => setShowResultsDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: 520,
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Search names"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {inputValue && (
                        <Tooltip title="Clear">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setInputValue("");
                                setOptions([]);
                                setHasMore(false);
                              }}
                              disabled={loading}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Search">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSearch}
                            disabled={loading}
                            sx={{
                              bgcolor: (theme) => theme.palette.primary.main,
                              color: (theme) => theme.palette.primary.contrastText,
                              "&:hover": {
                                bgcolor: (theme) => theme.palette.primary.dark,
                              },
                            }}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Search options">
                        <IconButton size="small" color="primary" onClick={() => setOptionsDialogOpen(true)}>
                          <TuneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {`Results: ${options.length}${hasMore ? "+" : ""}`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setIsAddOpen(true)}
              >
                Add New Name
              </Button>
            </Stack>
            {hasMore && (
              <Button
                size="small"
                variant="text"
                onClick={handleLoadMore}
                disabled={loadingMore}
                startIcon={<SearchIcon fontSize="small" />}
                sx={{ alignSelf: "flex-start" }}
              >
                {loadingMore ? "Loading..." : "Load more results"}
              </Button>
            )}
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && options.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                該当する学名がありません。
              </Typography>
            )}
            <List dense disablePadding>
              {options.map((opt) => (
                <ListItemButton
                  key={opt.id}
                  onClick={() => {
                    applySelection(opt);
                    setShowResultsDialog(false);
                  }}
                >
                  <ListItemText
                    primary={formatLabel(opt)}
                    secondary={opt.current_rank || opt._parent_label || ""}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResultsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={optionsDialogOpen}
        onClose={() => setOptionsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search Options</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Combine</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={combineMode}
                onChange={(_, v) => v && setCombineMode(v)}
              >
                <ToggleButton value="AND">AND</ToggleButton>
                <ToggleButton value="OR">OR</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Match</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={matchMode}
                onChange={(_, v) => v && setMatchMode(v)}
              >
                <ToggleButton value="partial">部分一致</ToggleButton>
                <ToggleButton value="exact">完全一致</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Fuzzy</Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fuzzyEnabled}
                    onChange={(e) => setFuzzyEnabled(e.target.checked)}
                  />
                }
                label="スペルゆらぎ"
                sx={{ ml: 0 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
